import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule, FormBuilder, FormGroup} from '@angular/forms';
import {FarmService} from '@services/farm.service';
import {Subscription} from 'rxjs';

export interface FilterField {
    key: string;
    label: string;
    type: 'text' | 'select' | 'date' | 'number';
    placeholder?: string;
    options?: {value: any; label: string}[];
    required?: boolean;
}

export interface FilterConfig {
    fields: FilterField[];
    showFarmFilter?: boolean;
    showHouseFilter?: boolean;
    // When enabled, shows a Partition dropdown that depends on selected House
    showPartitionFilter?: boolean;
}

@Component({
    selector: 'app-search-filter',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './search-filter.component.html',
    styleUrl: './search-filter.component.scss'
})
export class SearchFilterComponent implements OnInit, OnDestroy {
    @Input() config: FilterConfig = {fields: []};
    @Input() placeholder: string = 'Filters';
    @Input() userId: number | null = null;
    @Input() externalForm: FormGroup | null = null;

    @Output() filter = new EventEmitter<any>();
    @Output() clear = new EventEmitter<void>();

    public filterForm: FormGroup;
    public farmlist: any[] = [];
    public farmhouselist: any[] = [];
    public partitionlist: any[] = [];
    private subscriptions: Subscription[] = [];

    constructor(
        private fb: FormBuilder,
        private farmService: FarmService
    ) {
        this.filterForm = this.fb.group({});
    }

    ngOnInit(): void {
        // Use external form if provided, otherwise create internal form
        if (this.externalForm) {
            this.filterForm = this.externalForm;
        } else {
            this.initializeForm();
        }

        if (
            this.config.showFarmFilter &&
            typeof this.userId === 'number' &&
            this.userId > 0
        ) {
            this.loadFarmList();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    private initializeForm(): void {
        const formControls: any = {};

        // Add standard farm and house controls if enabled
        if (this.config.showFarmFilter) {
            formControls['farmId'] = [0];
        }
        if (this.config.showHouseFilter) {
            formControls['farmHouseId'] = [0];
        }
        if (this.config.showPartitionFilter) {
            formControls['partitionId'] = [0];
        }

        // Add custom fields from config
        this.config.fields.forEach((field) => {
            formControls[field.key] = [null];
        });

        this.filterForm = this.fb.group(formControls);
    }

    private loadFarmList(): void {
        if (typeof this.userId === 'number' && this.userId > 0) {
            const sub = this.farmService.getFarmList(this.userId).subscribe({
                next: (result) => {
                    this.farmlist = result;
                },
                error: () => {
                    this.farmlist = [];
                }
            });

            this.subscriptions.push(sub);
        }
    }

    onFarmChange(farmId: number): void {
        // Normalize to number
        const parsedFarmId =
            typeof farmId === 'string' ? parseInt(farmId, 10) : farmId;
        this.farmhouselist = []; // Clear house list
        this.partitionlist = []; // Clear partition list
        // Reset house & partition selection
        this.filterForm.patchValue({farmHouseId: 0});
        if (this.config.showPartitionFilter) {
            this.filterForm.patchValue({partitionId: 0});
        }

        if (parsedFarmId > 0) {
            const sub = this.farmService
                .getFarmHouseList(parsedFarmId)
                .subscribe((result) => {
                    this.farmhouselist = result;
                });
            this.subscriptions.push(sub);
        }
    }

    onHouseChange(farmHouseId: number): void {
        // Normalize to number
        const parsedHouseId =
            typeof farmHouseId === 'string'
                ? parseInt(farmHouseId, 10)
                : farmHouseId;
        this.partitionlist = []; // Clear partition list
        if (this.config.showPartitionFilter) {
            this.filterForm.patchValue({partitionId: 0});
        }

        if (parsedHouseId > 0 && this.config.showPartitionFilter) {
            const sub = this.farmService
                .getFarmHousePartitionList(parsedHouseId)
                .subscribe((result) => {
                    this.partitionlist = result;
                });
            this.subscriptions.push(sub);
        }
    }

    clearFilter(): void {
        if (this.externalForm) {
            // If using external form, reset only the fields we manage
            this.config.fields.forEach((field) => {
                this.filterForm.patchValue({[field.key]: null});
            });
            if (this.config.showFarmFilter) {
                this.filterForm.patchValue({farmId: 0});
            }
            if (this.config.showHouseFilter) {
                this.filterForm.patchValue({farmHouseId: 0});
            }
            if (this.config.showPartitionFilter) {
                this.filterForm.patchValue({partitionId: 0});
            }
        } else {
            // If using internal form, reset everything
            this.filterForm.reset();

            // Reset select fields to proper initial values
            if (this.config.showFarmFilter) {
                this.filterForm.patchValue({farmId: 0});
            }
            if (this.config.showHouseFilter) {
                this.filterForm.patchValue({farmHouseId: 0});
            }
            if (this.config.showPartitionFilter) {
                this.filterForm.patchValue({partitionId: 0});
            }

            // Reset custom fields to null
            this.config.fields.forEach((field) => {
                this.filterForm.patchValue({[field.key]: null});
            });
        }

        this.farmhouselist = [];
        this.partitionlist = [];
        this.clear.emit();
    }

    onInlineFilterChange(): void {
        // Emit search immediately when any filter changes
        const filterValues = this.filterForm.value;

        // Filter out empty string values for select fields
        const cleanedValues = Object.keys(filterValues).reduce((acc, key) => {
            const value = filterValues[key];
            const shouldExcludeValue = value === 0 || value === '0';
            if (
                value !== '' &&
                value !== null &&
                value !== undefined &&
                !shouldExcludeValue
            ) {
                acc[key] = value;
            }
            return acc;
        }, {} as any);

        if (this.config.showFarmFilter) {
            const fidRaw = this.filterForm.value['farmId'];
            const fid =
                typeof fidRaw === 'string' ? parseInt(fidRaw, 10) : fidRaw;
            const farm = this.farmlist.find((f) => f.id === fid);
            if (farm) cleanedValues['farmName'] = farm.name;
        }
        if (this.config.showHouseFilter) {
            const hidRaw = this.filterForm.value['farmHouseId'];
            const hid =
                typeof hidRaw === 'string' ? parseInt(hidRaw, 10) : hidRaw;
            const house = this.farmhouselist.find((h) => h.id === hid);
            if (house) cleanedValues['farmHouseName'] = house.name;
        }
        if (this.config.showPartitionFilter) {
            const pidRaw = this.filterForm.value['partitionId'];
            const pid =
                typeof pidRaw === 'string' ? parseInt(pidRaw, 10) : pidRaw;
            const part = this.partitionlist.find((p) => p.id === pid);
            if (part) cleanedValues['partitionName'] = part.name;
        }

        this.filter.emit(cleanedValues);
    }

    getFieldOptions(field: FilterField): {value: any; label: string}[] {
        return field.options || [];
    }
}
